import { Box, Skeleton, TextField, Typography } from '@mui/material';
import { FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import FieldEditComponent from 'components/Atoms/Form/FieldEditComponent/FieldEditComponent';
import { UpdateCompanyMutationFn, UpdateMyCompanyMutationFn } from 'src/graphql/generated/hooks';
import { UpdateMyCompanyAddressInput } from 'src/graphql/generated/schema';

import { AdminCompanyDetailsQuery, MyCompanyDetailsQuery } from '../../graphql/generated/operations';

type FormInput = Pick<UpdateMyCompanyAddressInput, 'street' | 'houseNumber' | 'zipCode' | 'city' | 'country'>;

interface EditCompanyAddressProps {
  data: MyCompanyDetailsQuery['me'] | AdminCompanyDetailsQuery | undefined;
  loading: boolean;
  mutation: UpdateMyCompanyMutationFn | UpdateCompanyMutationFn;
  companyId?: string;
}

const EditCompanyAddress: FC<EditCompanyAddressProps> = ({ companyId, data, loading, mutation }) => {
  const { t } = useTranslation('translation');

  const { getValues, control, reset } = useForm<FormInput>({
    values: {
      street: data?.company.address?.street ?? '',
      houseNumber: data?.company.address?.houseNumber ?? '',
      zipCode: data?.company.address?.zipCode ?? '',
      city: data?.company.address?.city ?? '',
      country: data?.company.address?.country ?? '',
    },
  });

  const handleSubmitAddress = useCallback(() => {
    mutation({
      variables: {
        input: {
          companyId,
          address: {
            ...getValues(),
          },
        },
      },
      update(_, { data }) {
        if (data) {
          toast.success(t('messages.success.company-address-updated'));
        }
      },
    });
  }, [companyId, getValues, mutation, t]);

  if (loading || !data) {
    return (
      <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    );
  }

  return (
    <FieldEditComponent
      title={t('pages.company-details-page.address')}
      value={
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography>
            <>
              {getValues('street')} {getValues('houseNumber')}
            </>
          </Typography>
          <Typography>
            <>
              {getValues('zipCode')} {getValues('city')}
            </>
          </Typography>
          <Typography>{getValues('country')}</Typography>
        </Box>
      }
      onCancel={reset}
      onSave={() => handleSubmitAddress()}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'space-between' }}>
          <Controller
            control={control}
            defaultValue={data.company.address?.street}
            name="street"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                inputProps={{ 'data-testid': 'input-company-street' }}
                label={t('pages.company-details-page.placeholders.street')}
                sx={{ flex: '0 0 80%' }}
              />
            )}
            rules={{ required: true }}
          />

          <Controller
            control={control}
            defaultValue={data.company.address?.houseNumber}
            name="houseNumber"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                inputProps={{ 'data-testid': 'input-company-houseNumber' }}
                label={t('pages.company-details-page.placeholders.house-number')}
              />
            )}
            rules={{ required: true }}
          />
        </Box>

        <Controller
          control={control}
          defaultValue={data.company.address?.zipCode}
          name="zipCode"
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              inputProps={{ 'data-testid': 'input-company-zip-code' }}
              label={t('pages.company-details-page.placeholders.zip-code')}
            />
          )}
          rules={{ required: true }}
        />

        <Controller
          control={control}
          defaultValue={data.company.address?.city}
          name="city"
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              inputProps={{ 'data-testid': 'input-company-city' }}
              label={t('pages.company-details-page.placeholders.city')}
            />
          )}
          rules={{ required: true }}
        />

        <Controller
          control={control}
          defaultValue={data.company.address?.country}
          name="country"
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              inputProps={{ 'data-testid': 'input-company-country' }}
              label={t('pages.company-details-page.placeholders.country')}
            />
          )}
          rules={{ required: true }}
        />
      </Box>
    </FieldEditComponent>
  );
};

export default EditCompanyAddress;
